import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

import { userLocale } from '@/utils/locale';

dayjs.extend(quarterOfYear);

/**
 * A mapping from locale codes to dynamic import functions for Day.js locales.
 * Add more locales as needed.
 * @type {Object.<string, function(): Promise<any>>}
 */
const localeImports = {
  az: () => import('dayjs/locale/az'),
  de: () => import('dayjs/locale/de'),
  'de-at': () => import('dayjs/locale/de-at'),
  'de-ch': () => import('dayjs/locale/de-ch'),
  en: () => import('dayjs/locale/en'),
  'en-gb': () => import('dayjs/locale/en-gb'),
  el: () => import('dayjs/locale/el'),
  es: () => import('dayjs/locale/es'),
  fr: () => import('dayjs/locale/fr'),
  'fr-ca': () => import('dayjs/locale/fr-ca'),
  'fr-ch': () => import('dayjs/locale/fr-ch'),
  it: () => import('dayjs/locale/it'),
  'it-ch': () => import('dayjs/locale/it-ch'),
  nl: () => import('dayjs/locale/nl'),
  'nl-be': () => import('dayjs/locale/nl-be'),
  pt: () => import('dayjs/locale/pt'),
  uk: () => import('dayjs/locale/uk'),
  // Add more supported locales here
};

/**
 * Dynamically imports and sets the Day.js locale.
 *
 * Attempts to load the Day.js locale file for the given locale (e.g., "en-US" or "en-gb").
 * If the region-specific locale is unavailable, it falls back to the base language (e.g., "en").
 * If neither can be loaded, it defaults to English ("en").
 * Returns the actual locale that was successfully imported.
 *
 * @async
 * @function importDayjsLocale
 * @param {string} [locale=userLocale] - The locale code to use (e.g., "en", "en-gb", "es").
 * @returns {Promise<string>} The locale string that was successfully imported and set (or "en" as fallback).
 *
 * @example
 * const loadedLocale = await importDayjsLocale('es');
 * // loadedLocale will be 'es' or 'en' depending on availability
 */
export async function importDayjsLocale(locale = userLocale) {
  let importedLocale = 'en'; // fallback value

  // Normalize locale: try full, then base (e.g., "en-US" -> "en-us" -> "en")
  const localesToTry = [
    locale.toLowerCase(), // e.g., "en-gb"
    locale.split('-')[0].toLowerCase(), // e.g., "en"
  ];

  for (const l of localesToTry) {
    if (localeImports[l]) {
      try {
        await localeImports[l]();
        importedLocale = l;
        break;
      } catch (err) {
        console.error(`Failed to import Day.js locale "${l}":`, err);
      }
    }
  }

  return importedLocale;
}

/**
 * Formats a date according to the specified format
 * @param {string} date - The date to format
 * @param {string} [format='DD/MM/YYYY'] - The format to use
 * @returns {string} The formatted date
 */
export const formatDate = (date, format = 'L') => {
  if (!date) return '';
  return dayjs(date).format(format);
};

/**
 * Formats a date range for display, collapsing to a single date when both ends match.
 * @param {string} dateFrom
 * @param {string} dateTo
 * @returns {string} e.g. "Jan 1, 2024" or "Jan 1, 2024 - Jan 5, 2024"
 */
export const formatDateRangeLabel = (dateFrom, dateTo) => {
  if (dateFrom === dateTo) {
    return formatDate(dateTo);
  }
  return [formatDate(dateFrom), formatDate(dateTo)].filter(Boolean).join(' - ');
};

/**
 * Compares two dates for sorting
 * @param {string} date1 - The first date
 * @param {string} date2 - The second date
 * @returns {number} -1 if date1 is before date2, 1 if date1 is after date2, 0 if they are the same
 */
export const compareDates = (date1, date2) => {
  if (!date1 || !date2) return 0;
  const a = dayjs(date1);
  const b = dayjs(date2);
  return a.isBefore(b) ? -1 : a.isAfter(b) ? 1 : 0;
};

const dateRangeFor = (unit, base) => [base.startOf(unit).format('YYYY-MM-DD'), base.endOf(unit).format('YYYY-MM-DD')];

/**
 * Quick date-range shortcuts relative to today: month to date, year to date (each running
 * from the start of the period through today), last full calendar month, and last full quarter.
 * @returns {Array<{label: string, dates: [string, string]}>}
 */
export const getQuickDateRangeOptions = () => {
  const today = dayjs();
  const todayFormatted = today.format('YYYY-MM-DD');
  const lastMonth = today.subtract(1, 'month');
  const lastQuarter = today.subtract(1, 'quarter');

  return [
    { label: 'Month to date', dates: [today.startOf('month').format('YYYY-MM-DD'), todayFormatted] },
    { label: 'Last full month', dates: dateRangeFor('month', lastMonth) },
    { label: 'Last quarter', dates: dateRangeFor('quarter', lastQuarter) },
    { label: 'Year to date', dates: [today.startOf('year').format('YYYY-MM-DD'), todayFormatted] },
  ];
};
